import React, { useEffect, useState } from 'react';
import { Redirect, Route } from 'react-router-dom';
import { PlaceFactory, PlaceList } from './models/factories/PlaceFactory';
import { db, syncWithRecovery } from './models/DBUtils';
import { ExplorerAppData, ExplorerContext } from './application/config';
import { useAppDispatch } from './state/hooks';
/* IONIC Components */
import { IonApp, IonRouterOutlet, setupIonicReact } from '@ionic/react';
import { IonAlert } from '@ionic/react';
import { IonReactRouter } from '@ionic/react-router';

//import type { AppState } from './state/store';


/* Page Components */
import Home from './pages/Home';
import Stories from './pages/Stories';
import Bookmarks from './pages/Bookmarks';
import StoryPage from './pages/innerPages/StoryPage';
import PlacePage from './pages/innerPages/PlacePage';
import TrailPage from './pages/innerPages/TrailPage';
import Profile from './pages/Profile';
import PlaceMap from './pages/PlaceMap';

/* Core CSS required for Ionic components to work properly */
import '@ionic/react/css/core.css';

/* Basic CSS for apps built with Ionic */
import '@ionic/react/css/normalize.css';
import '@ionic/react/css/structure.css';
import '@ionic/react/css/typography.css';

/* Optional CSS utils that can be commented out */
import '@ionic/react/css/padding.css';
import '@ionic/react/css/float-elements.css';
import '@ionic/react/css/text-alignment.css';
import '@ionic/react/css/text-transformation.css';
import '@ionic/react/css/flex-utils.css';
import '@ionic/react/css/display.css';

/* Theme variables */
import './theme/variables.css';

/* Custom Components */
import CustomTabs from './components/CustomTabs/CustomTabs';
import { StoryFactory, StoryList } from './models/factories/StoryFactory';
import { TrailFactory, TrailList } from './models/factories/TrailFactory';
import { Story } from './models/Story';
import AddToHomePrompt from './components/AddToHomePrompt/AddToHomePrompt';
import {
	ActivityFactory,
	ActivityList,
} from './models/factories/ActivityFactory';
import { TopicFactory, TopicList } from './models/factories/TopicFactory';
import SearchResults from './pages/innerPages/SearchResults';

import { isProbablyDesktop } from './device-detect';
import { UPDATE_LOCATION } from './state/locationSlice';
import { AONB_Bounds } from './application/config';

setupIonicReact();

const FORCE_RESET_STORAGE_KEY = 'force_reset_version';
const FORCE_RESET_VERSION = '2026-03-06-route-fix-1';
const MAX_BROWSER_LOCATION_ACCURACY_METERS = 10000;

const App: React.FC = () => {
	const [dbReady, setDbReady] = useState(false);
	const [appReady, setAppReady] = useState(false);
	const dispatch = useAppDispatch();
	const emptyPlaceList = PlaceFactory.getEmpty();
	const emptyStoryList = StoryFactory.getEmpty();
	const emptyTrailList = TrailFactory.getEmpty();
	const emptyActivityList = ActivityFactory.getEmpty();
	const emptyTopicList = TopicFactory.getEmpty();
	const [AppData, setAppData] = useState(ExplorerAppData);
	const [PlaceResult, setPlaceResult] = useState<PlaceList>(emptyPlaceList);
	const [StoryResult, setStoryResult] = useState<StoryList>(emptyStoryList);
	const [RecentStoryResult, setRecentStoryResult] =
		useState<StoryList>(emptyStoryList);
	const [TrailResult, setTrailResult] = useState<TrailList>(emptyTrailList);
	const [ActivityResult, setActivityResult] =
		useState<ActivityList>(emptyActivityList);
	const [TopicResult, setTopicResult] = useState<TopicList>(emptyTopicList);
	const [featuredStories, setFeaturedStories] = useState<Story[]>(
		emptyStoryList.stories,
	);
	const [recentStories, setRecentStories] = useState<Story[]>(
		emptyStoryList.stories,
	);
	// Redux
	//const location = useAppSelector((state) => state.location, shallowEqual);

	const [showAlert, setShowAlert] = useState(false);

	const runForcedResetIfNeeded = async (): Promise<boolean> => {
		const currentVersion = localStorage.getItem(FORCE_RESET_STORAGE_KEY);
		if (currentVersion === FORCE_RESET_VERSION) {
			return false;
		}

		try {
			await db.delete();
		} catch (error) {
			console.warn('forced_reset_db_delete_failed', error);
		}

		if ('caches' in window) {
			try {
				const cacheKeys = await caches.keys();
				await Promise.all(cacheKeys.map((key) => caches.delete(key)));
			} catch (error) {
				console.warn('forced_reset_cache_delete_failed', error);
			}
		}

		if ('serviceWorker' in navigator) {
			try {
				const registrations =
					await navigator.serviceWorker.getRegistrations();
				await Promise.all(
					registrations.map((registration) =>
						registration.unregister(),
					),
				);
			} catch (error) {
				console.warn('forced_reset_sw_unregister_failed', error);
			}
		}

		try {
			window.sessionStorage?.clear();
		} catch (error) {
			console.warn('forced_reset_storage_clear_failed', error);
		}

		window.localStorage?.setItem(
			FORCE_RESET_STORAGE_KEY,
			FORCE_RESET_VERSION,
		);
		window.location.reload();
		return true;
	};

	useEffect(() => {
		const dismissed =
			localStorage.getItem('desktopNoticeDismissed') === '1';
		if (!dismissed && isProbablyDesktop()) {
			setShowAlert(true);
		}
	}, []);

	const get_places = async () => {
		// console.log('Getting Places');
		const query_results = await PlaceFactory.getAll();
		setPlaceResult(query_results);
	};
	const get_stories = async () => {
		// console.log('Getting Stories');
		const query_results = await StoryFactory.getAll();
		setStoryResult(query_results);
	};
	const get_recent_stories = async () => {
		// console.log('Getting Stories');
		const query_results = await StoryFactory.getRecent();
		setRecentStoryResult(query_results);
	};
	const get_trails = async () => {
		// console.log('Getting Trails');
		const query_results = await TrailFactory.getAll();
		setTrailResult(query_results);
	};
	const get_activities = async () => {
		const query_results = await ActivityFactory.getAll();
		setActivityResult(query_results);
	};
	const get_topics = async () => {
		const query_results = await TopicFactory.getAll();
		setTopicResult(query_results);
	};

	useEffect(() => {
		let isMounted = true;
		const hydrate = async () => {
			setDbReady(false);
			const didTriggerForcedReset = await runForcedResetIfNeeded();
			if (didTriggerForcedReset) {
				return;
			}

			await syncWithRecovery();

			try {
				await Promise.all([
					get_places(),
					get_stories(),
					get_recent_stories(),
					get_trails(),
					get_activities(),
					get_topics(),
				]);
			} catch (error) {
				console.error('Failed to load local data', error);
			} finally {
				if (isMounted) {
					setDbReady(true);
				}
			}
		};

		hydrate();

		return () => {
			isMounted = false;
		};
	}, []);

	useEffect(() => {
		if (
			typeof window !== 'undefined' &&
			!window.isSecureContext
		) {
			console.log(
				'Browser geolocation requires a secure origin; location request skipped',
			);
			return;
		}

		if (!('geolocation' in navigator)) {
			console.log('Geolocation not supported in this browser');
			return;
		}

		const updateLocation = (coords: GeolocationCoordinates) => {
			if (
				!Number.isFinite(coords.latitude) ||
				!Number.isFinite(coords.longitude)
			) {
				return;
			}

			const accuracy = coords.accuracy ?? 0;
			if (
				!Number.isFinite(accuracy) ||
				accuracy <= 0 ||
				accuracy > MAX_BROWSER_LOCATION_ACCURACY_METERS
			) {
				console.log(
					'Browser geolocation ignored due to low precision',
					accuracy,
				);
				return;
			}

			const nextLatLng = {
				lat: coords.latitude,
				lng: coords.longitude,
			};
			if (!AONB_Bounds.pad(0.35).contains(nextLatLng)) {
				console.log(
					'Browser geolocation ignored outside app area',
					nextLatLng,
				);
				return;
			}

			dispatch({
				type: UPDATE_LOCATION,
				payload: {
					latitude: coords.latitude,
					longitude: coords.longitude,
					accuracy,
				},
			});
		};

		let watchId: number | null = null;

		navigator.geolocation.getCurrentPosition(
			({ coords }) => {
				updateLocation(coords);
				watchId = navigator.geolocation.watchPosition(
					({ coords: watchCoords }) => updateLocation(watchCoords),
					(browserError) => {
						console.log(
							'Browser geolocation watch failed',
							browserError,
						);
					},
					{
						enableHighAccuracy: true,
						timeout: 15000,
						maximumAge: 30000,
					},
				);
			},
			(browserError) => {
				console.log('Initial browser geolocation failed', browserError);
			},
			{ enableHighAccuracy: true, timeout: 15000, maximumAge: 0 },
		);

		return () => {
			if (watchId !== null) {
				navigator.geolocation.clearWatch(watchId);
			}
		};
	}, [dispatch]);

	useEffect(() => {
		// Geolocation.checkPermissions()
		// 	.then(
		// 		(permissions) => {
		// 			try {
		// 				Geolocation.getCurrentPosition().then(
		// 					(locationResult) => {
		// 						const new_location = new LatLng(
		// 							locationResult.coords.latitude,
		// 							locationResult.coords.longitude,
		// 						);
		// 						const current_location = new LatLng(
		// 							location.latitude,
		// 							location.longitude,
		// 						);
		// 						if (
		// 							current_location.distanceTo(new_location) >
		// 							10
		// 						) {
		// 							console.log(
		// 								'Geolocation moved > 10m, storing',
		// 							);
		// 							dispatch({
		// 								type: UPDATE_LOCATION,
		// 								payload: {
		// 									latitude:
		// 										locationResult.coords.latitude,
		// 									longitude:
		// 										locationResult.coords.longitude,
		// 									accuracy: 0,
		// 								},
		// 							} as LocationReducerAction);
		// 						} else {
		// 							console.log(
		// 								'Geolocation moved < 10m, ignoring',
		// 							);
		// 						}
		// 					},
		// 				);
		// 			} catch (err) {
		// 				console.log('Geo Permissions Error', err);
		// 			}
		// 		},
		// 		(err) => {
		// 			console.log('Geo Permissions Rejected', err);
		// 		},
		// 	)
		// 	.catch((err) => {
		// 		console.log('Geo Permissions Failed', err);
		// 	});
	}, []);

	useEffect(() => {
		if (!dbReady) {
			return;
		}

		setAppData(prev => ({
			...prev,
			features: {
				...(prev.features ?? {}),
				all_places: PlaceResult,
				all_stories: StoryResult,
				all_trails: TrailResult,
				all_activities: ActivityResult,
				all_topics: TopicResult,
			},
		}));

		setAppReady(true);
	}, [
		dbReady,
		PlaceResult,
		StoryResult,
		TrailResult,
		ActivityResult,
		TopicResult,
	]);

	// Set Featured Stories
	useEffect(() => {
		const featuredstories = StoryResult.stories
			.filter((story) => story.featured)
			.reverse();
		setFeaturedStories(featuredstories);
		const recentStories = RecentStoryResult.stories;
		setRecentStories(recentStories);
	}, [RecentStoryResult, StoryResult, ActivityResult, TopicResult]);

	return (
		<IonApp>
			<AddToHomePrompt />
			{/* <IonToggle
				className="network-toggler"
				enableOnOffLabels={true}
				checked={network.status}
				onClick={() => handleNetworkToggle()}></IonToggle> */}
			{/*<IonLoading isOpen={!appReady} message={'Preparing Database'} />*/}
			{appReady && (
				<ExplorerContext.Provider value={AppData}>
					<IonReactRouter>
						{/* TODO: CustomTabs dont want to be visible on innerpages */}
						<CustomTabs />
						<IonRouterOutlet>
							<Route exact path="/home">
								<Home
									places={PlaceResult}
									stories={featuredStories}
									recentStories={recentStories}
									activities={ActivityResult.activities}
								/>
							</Route>
							<Route exact path="/">
								<Redirect to="/home" />
							</Route>
							<Route exact path="/placemap">
								<PlaceMap places={PlaceResult} />
								{/*<DumbMap places={PlaceResult.places} />*/}
							</Route>
							<Route exact path="/stories">
								<Stories stories={StoryResult} />
							</Route>
							<Route exact path="/bookmarks">
								<Bookmarks />
							</Route>

							{/* PlaceMap Inner Routes */}
							<Route exact path="/story/:storyId">
								<StoryPage />
							</Route>
							<Route exact path="/place/:placeId">
								<PlacePage />
							</Route>
							<Route exact path="/trail/:trailId">
								<TrailPage />
							</Route>
							<Route exact path="/search-results">
								<SearchResults />
							</Route>
							<Route exact path="/profile">
								<Profile
									activities={ActivityResult.activities}
									topics={TopicResult.topics}
								/>
							</Route>
							<Route>
								<Redirect to="/home" />
							</Route>
						</IonRouterOutlet>
					</IonReactRouter>
				</ExplorerContext.Provider>
			)}

			<IonAlert
				isOpen={showAlert}
				header="Best on mobile"
				message="This app is optimised for use on a mobile whilst out on the trail. You can still browse on desktop, but live GPS works best on a phone."
				buttons={[
					{
						text: 'OK',
						role: 'confirm',
						handler: () => {
							//localStorage.setItem('desktopNoticeDismissed', '1');
							setShowAlert(false);
						},
					},
					{
						text: 'Don’t show again',
						handler: () => {
							localStorage.setItem('desktopNoticeDismissed', '1');
							setShowAlert(false);
						},
					},
				]}
				onDidDismiss={() => setShowAlert(false)}
			/>
		</IonApp>
	);
};

export default App;
